import {noop} from '@benchling/js-utils';
import React, {useState} from 'react';
import isEqual from 'react-fast-compare';

import {assert, assertAndContinue} from 'Asserts';
import {Owner} from 'auth/PermissionConstants';
import * as Context from 'Context';
import {ActionOrigin, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {
  NameTemplatePart,
  NameTemplateType,
  nameTemplateTypeToFriendlyName,
} from 'name-templates/NameTemplateConstants';
import {graphQLNameTemplatePartsToProps} from 'name-templates/NameTemplateUtil';
import createStudyTypeNameTemplateTypeConfiguration from 'name-templates/StudyTypeNameTemplateTypeConfiguration';
import notifications from 'notifications';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {massageFieldsForSubmit} from 'schemas/SchemaUtil';
import {
  SetNameTemplateProps,
  SetNameTemplateResponse,
  setProcedureRunNameTemplateParts,
  setStudyNameTemplateParts,
  StudySchemaSpec,
  updateStudySchema,
} from 'studies/study-schemas/StudySchemaDAO';
import {withSpecErrorHandling} from 'studies/study-schemas/StudySchemaDAO';
import StudySchemaMetadataSettings from 'studies/study-schemas/StudySchemaMetadataSettings';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import {withLoadingScreen} from 'util/Promises';

import {
  StudySchemaQuery,
  StudySchemaQuery_studySchema_StudySchemaNode_entryTemplates as EntryTemplateIconOrInaccessibleInfo,
} from './__generated/StudySchemaQuery';
import {massageEntryTemplatesForSubmit} from './StudySchemaCreateFlow';
import StudySchemaPermissionSettings from './StudySchemaPermissionSettings';

type StudySchema = ExtractByTypename<StudySchemaQuery['studySchema'], 'StudySchemaNode'>;

enum Tabs {
  STUDY_SCHEMA_EDITOR = 'STUDY_SCHEMA_EDITOR',
  STUDY_SCHEMA_ACCESS_POLICIES = 'STUDY_SCHEMA_ACCESS_POLICIES',
}

export function getSpecForRequest(
  fields: Array<ClientSchemaField>,
  idPrefix: string,
  isDesignApprovalRequired: boolean,
  entryTemplates: Array<EntryTemplateIconOrInaccessibleInfo>,
  studyType: StudyType
): StudySchemaSpec {
  return {
    fields: massageFieldsForSubmit(fields),
    idPrefix,
    isDesignApprovalRequired,
    template_api_ids: massageEntryTemplatesForSubmit(entryTemplates),
    studyType,
  };
}

export interface ExistingStudySchemaEditorProps {
  schema: StudySchema;
  ownerChoices: Array<Owner>;
  refetch: () => void;
}
export default function ExistingStudySchemaEditor(props: ExistingStudySchemaEditorProps): JSX.Element {
  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.STUDY_SCHEMA_EDITOR);
  const updateSchema = ({
    name,
    sqlIdentifier,
    spec,
  }: {
    name?: string;
    sqlIdentifier?: string;
    spec?: StudySchemaSpec;
  }): void => {
    withLoadingScreen(
      (async () => {
        await Context.usingAsync(
          {analyticsMetadata: {actionOrigin: ActionOrigin.SETTINGS_STUDY_SCHEMA_PAGE}},
          null,
          async () => {
            try {
              const response = await withSpecErrorHandling(
                updateStudySchema({
                  schemaId: props.schema.id,
                  name: name || props.schema.name,
                  sqlIdentifier: sqlIdentifier || props.schema.sqlIdentifier,
                  spec:
                    spec ||
                    getSpecForRequest(
                      props.schema.allFields,
                      props.schema.prefix,
                      props.schema.isDesignApprovalRequired,
                      props.schema.entryTemplates,
                      props.schema.studyType
                    ),
                })
              );
              if (response != null) {
                notifications.success('Updated study schema successfully');
              }
            } finally {
              // Always refetch. On failure, refetch to avoid retaining changes that failed to save in the UI
              props.refetch();
            }
          }
        );
      })(),
      'Updating study schema...'
    );
  };

  const getSetNameTemplatePartsFunction = (
    nameTemplateType: NameTemplateType.STUDY | NameTemplateType.PROCEDURE_RUN
  ): ((props: SetNameTemplateProps) => Promise<SetNameTemplateResponse>) => {
    switch (nameTemplateType) {
      case NameTemplateType.STUDY:
        return setStudyNameTemplateParts;
      case NameTemplateType.PROCEDURE_RUN:
        return setProcedureRunNameTemplateParts;
      default:
        assert(false, `Unexpected name template type ${nameTemplateType}`);
    }
  };

  const setNameTemplate = (
    nameTemplateType: NameTemplateType.STUDY | NameTemplateType.PROCEDURE_RUN,
    parts: Array<NameTemplatePart>
  ): Promise<boolean> => {
    const setNameTemplatePartsFunction = getSetNameTemplatePartsFunction(nameTemplateType);
    assert(
      nameTemplateType !== NameTemplateType.PROCEDURE_RUN || props.schema.studyType !== StudyType.CUSTOM,
      'Procedure run name templates are not supported for custom study types'
    );
    const nameTemplateTypeConfiguration = createStudyTypeNameTemplateTypeConfiguration(
      props.schema.studyType
    );
    const nameTemplateDisplayName = nameTemplateTypeToFriendlyName(
      nameTemplateType,
      nameTemplateTypeConfiguration
    );

    return withLoadingScreen(
      (async () => {
        assert(setNameTemplatePartsFunction != null, 'setNameTemplatePartsFunction should be set');
        const result = await Context.usingAsync(
          {analyticsMetadata: {actionOrigin: ActionOrigin.SETTINGS_STUDY_SCHEMA_PAGE}},
          null,
          async () => {
            const response = await withSpecErrorHandling(
              setNameTemplatePartsFunction({schemaId: props.schema.id, nameTemplateParts: parts})
            );
            if (response == null) {
              // Refetch to avoid retaining changes that failed to save in the UI
              props.refetch();
              return false;
            }
            notifications.success(`Updated ${nameTemplateDisplayName} name template successfully`);
            props.refetch();
            return true;
          }
        );
        return result;
      })(),
      `Updating ${nameTemplateDisplayName} name template...`
    );
  };

  const setName = (name: string): void => {
    updateSchema({name});
  };

  const setPrefix = (prefix: string): void => {
    updateSchema({
      spec: getSpecForRequest(
        props.schema.allFields,
        prefix,
        props.schema.isDesignApprovalRequired,
        props.schema.entryTemplates,
        props.schema.studyType
      ),
    });
  };

  const setSqlIdentifier = (sqlIdentifier: string): void => {
    updateSchema({sqlIdentifier});
  };

  const setEntryTemplates = (entryTemplates: Array<EntryTemplateIconOrInaccessibleInfo>): void => {
    updateSchema({
      spec: getSpecForRequest(
        props.schema.allFields,
        props.schema.prefix,
        props.schema.isDesignApprovalRequired,
        entryTemplates,
        props.schema.studyType
      ),
    });
  };

  const handleFieldsChange = (newFields: Array<ClientSchemaField>): void => {
    const fieldsWithoutCid = newFields.map((field) => {
      const {cid, ...fieldWithoutCid} = field;
      return fieldWithoutCid;
    }) as Array<SchemaField>; // TODO(BNCH-30720) Fix SchemaField/ClientSchemaField type issues

    if (isEqual(fieldsWithoutCid, props.schema.allFields)) {
      return;
    }
    updateSchema({
      spec: getSpecForRequest(
        newFields,
        props.schema.prefix,
        props.schema.isDesignApprovalRequired,
        props.schema.entryTemplates,
        props.schema.studyType
      ),
    });
  };

  const setIsDesignApprovalRequired = (isDesignApprovalRequired: boolean): void => {
    updateSchema({
      spec: getSpecForRequest(
        props.schema.allFields,
        props.schema.prefix,
        isDesignApprovalRequired,
        props.schema.entryTemplates,
        props.schema.studyType
      ),
    });
  };

  const getNameTemplateParts = (nameTemplateType: NameTemplateType): Array<NameTemplatePart> => {
    const nameTemplates = props.schema.nameTemplates.filter(
      (template) => template.templateType === nameTemplateType
    );
    assertAndContinue(
      nameTemplates.length <= 1,
      `Study schema should have at most one ${nameTemplateType === NameTemplateType.STUDY ? 'study' : 'run'} name template`
    );
    return graphQLNameTemplatePartsToProps(
      nameTemplates.length > 0 ? nameTemplates[0].nameTemplateParts : []
    );
  };
  return (
    <TabContainer
      onRequestTabChange={(tab: Tabs) => {
        setActiveTab(tab);
      }}
      activeTab={activeTab}
      className="u-flex-grow"
    >
      <Tab tabKey={Tabs.STUDY_SCHEMA_EDITOR} title="Study Schema Settings">
        <StudySchemaMetadataSettings
          ownerId={props.schema.organization.id}
          setOwnerId={noop /* Owner can only be set on creation */}
          name={props.schema.name}
          setName={setName}
          studyNameTemplateParts={getNameTemplateParts(NameTemplateType.STUDY)}
          setStudyNameTemplateParts={(nameTemplateParts) =>
            setNameTemplate(NameTemplateType.STUDY, nameTemplateParts)
          }
          procedureRunNameTemplateParts={getNameTemplateParts(NameTemplateType.PROCEDURE_RUN)}
          setProcedureRunNameTemplateParts={(nameTemplateParts) =>
            setNameTemplate(NameTemplateType.PROCEDURE_RUN, nameTemplateParts)
          }
          prefix={props.schema.prefix}
          setPrefix={setPrefix}
          fields={props.schema.allFields}
          setFields={handleFieldsChange}
          isDesignApprovalRequired={props.schema.isDesignApprovalRequired}
          setIsDesignApprovalRequired={setIsDesignApprovalRequired}
          ownerChoices={props.ownerChoices}
          isBeingCreated={false}
          schemaId={props.schema.id}
          schema={props.schema}
          entryTemplates={props.schema.entryTemplates}
          setEntryTemplates={setEntryTemplates}
          studyType={props.schema.studyType}
          setStudyType={() => {
            throw new Error('User unexpectedly tried to set study type after creation');
          }}
          autoGenerateId={props.schema.prefix !== ''}
          setAutoGenerateId={() => {
            throw new Error('User unexpectedly tried to set auto-generate ID after creation');
          }}
          setSqlIdentifier={setSqlIdentifier}
        />
      </Tab>
      <Tab tabKey={Tabs.STUDY_SCHEMA_ACCESS_POLICIES} title="Access Policies">
        <StudySchemaPermissionSettings studySchemaId={props.schema.id} />
      </Tab>
    </TabContainer>
  );
}
